/**
 * Slow Pan Animation for Homepage Background
 *
 * Provides ambient motion for the Mapbox background on the homepage.
 * Continuously pans the map eastward at a slow, steady rate.
 *
 * @see docs/plans/prototype/MAPBOX_HYBRID_IMPLEMENTATION.md Phase 2.2
 */

import type mapboxgl from "mapbox-gl";
import { MAPBOX_CONFIG } from "./config";

let animationFrameId: ReturnType<typeof setTimeout> | null = null;

/**
 * Start slow continuous pan animation
 * Uses config values from MAPBOX_CONFIG.animation.slowPan
 * Uses Mapbox's easeTo for GPU-accelerated smooth motion
 *
 * @param map - Mapbox GL map instance
 */
export function startSlowPan(map: mapboxgl.Map): void {
  // Clear any existing animation to prevent multiple animations
  stopSlowPan();

  const { degreesPerInterval, intervalMs } = MAPBOX_CONFIG.animation.slowPan;

  // Use easeTo for smooth GPU-accelerated panning
  // This queues up continuous pan motions
  const panContinuously = () => {
    const center = map.getCenter();

    map.easeTo({
      center: [center.lng + degreesPerInterval, center.lat],
      duration: intervalMs,
      easing: (t) => t, // Linear easing for constant velocity
    });

    // Queue next pan before current one finishes for seamless motion
    animationFrameId = globalThis.setTimeout(() => {
      panContinuously();
    }, intervalMs);
  };

  panContinuously();
}

/**
 * Stop slow pan animation
 * Clears the timeout and halts all pan motion
 */
export function stopSlowPan(): void {
  if (animationFrameId !== null) {
    clearTimeout(animationFrameId);
    animationFrameId = null;
  }
}
